import { Injectable } from '@angular/core';
import type { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import type { DocumentHistoryPageInterface } from '@zaoblako/shared/modules/document/dtos/document-history/document-history-page.dto';
import type { DocumentBulkUpdateInterface } from '@zaoblako/shared/modules/document/dtos/document/document-bulk-update.dto';
import type { DocumentCreateInterface } from '@zaoblako/shared/modules/document/dtos/document/document-create.dto';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import type { DocumentFlatQueryInterface } from '@zaoblako/shared/modules/document/dtos/document/document-flat-query.dto';
import type { DocumentMultipleDeleteInterface } from '@zaoblako/shared/modules/document/dtos/document/document-multiple-delete.dto';
import type { DocumentPageInterface } from '@zaoblako/shared/modules/document/dtos/document/document-page.dto';
import type { DocumentQueryInterface } from '@zaoblako/shared/modules/document/dtos/document/document-query.dto';
import type { DocumentSplitInterface } from '@zaoblako/shared/modules/document/dtos/document/document-split.dto';
import type { DocumentStatisticsInterface } from '@zaoblako/shared/modules/document/dtos/document/document-statistics.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DocumentRegistryService extends BaseService {
  protected baseUrl = '/document/registry';

  public documents(queryParams: DocumentQueryInterface): Observable<DocumentPageInterface> {
    return this.http.get<DocumentPageInterface>(this.baseUrl, { params: this.objectToHttpParams(queryParams) });
  }

  public documentsAsExcel(queryParams: DocumentFlatQueryInterface): Observable<BlobPart> {
    return this.http.get<BlobPart>(`${this.baseUrl}/excel`, {
      params: this.objectToHttpParams(queryParams),
      responseType: 'arraybuffer' as any
    });
  }

  public updateMany(body: DocumentBulkUpdateInterface): Observable<DocumentFindInterface[]> {
    return this.http.put<DocumentFindInterface[]>(this.baseUrl, body);
  }

  public deleteMany(body: DocumentMultipleDeleteInterface): Observable<void> {
    return this.http.request<void>('delete', `${this.baseUrl}`, { body });
  }

  public statistics(queryParams: DocumentFlatQueryInterface): Observable<DocumentStatisticsInterface> {
    return this.http.get<DocumentStatisticsInterface>(`${this.baseUrl}/statistics`, {
      params: this.objectToHttpParams(queryParams)
    });
  }

  public updateByCode(code: string, data: DocumentCreateInterface): Observable<DocumentFindInterface> {
    return this.http.put<DocumentFindInterface>(this.baseUrl + '/' + code, data);
  }

  public createBasedOn(code: string): Observable<DocumentFindInterface> {
    return this.http.post<DocumentFindInterface>(`${this.baseUrl}/${code}/based-on`, {});
  }

  public fromBankStatement(formData: FormData): Observable<DocumentFindInterface[]> {
    return this.http.post<DocumentFindInterface[]>(`${this.baseUrl}/from-bank-statement`, formData);
  }

  public splitDocument(code: string, body: DocumentSplitInterface): Observable<DocumentFindInterface> {
    return this.http.post<DocumentFindInterface>(`${this.baseUrl}/${code}/split`, body);
  }

  public getHistory(code: string, queryParams?: PaginationQueryInterface): Observable<DocumentHistoryPageInterface> {
    return this.http.get<DocumentHistoryPageInterface>(`${this.baseUrl}/${code}/history`, {
      params: queryParams ? this.objectToHttpParams(queryParams) : {}
    });
  }

  public getRelatedDocuments(code: string): Observable<DocumentFindInterface[]> {
    return this.http.get<DocumentFindInterface[]>(`${this.baseUrl}/${code}/related`);
  }

  public downloadOneAsPdf(code: string): Observable<BlobPart> {
    return this.http.get<BlobPart>(`${this.baseUrl}/${code}/pdf`, {
      responseType: 'arraybuffer' as any
    });
  }

  public triggerQueue(): Observable<void> {
    return this.http.post<void>(`/finance${this.baseUrl}/trigger-queue`, null);
  }
}
